import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import api from "../lib/api";
import { BookOpen, ShoppingBag, Archive } from "lucide-react";
import SafeImage from "../components/SafeImage";

const fmt = (a, c) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(a || 0);

export default function Magazine() {
  const { t, tr, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/magazines").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const buyMagazine = async (m) => {
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/checkout/magazine/${m.id}`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      setBusyId(null);
    }
  };

  const hero = items[0];
  const others = items.slice(1);

  return (
    <div className="bg-[var(--gai-white)]" data-testid="page-magazine">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="overline mb-3">{t("home.overline")}</div>
        <h1 className="serif text-5xl md:text-7xl leading-none text-[var(--gai-black)]">{t("magazine.title")}</h1>
        <p className="mt-4 max-w-xl text-lg text-[var(--gai-grey-700)]">{t("magazine.sub")}</p>

        <div className="gai-hairline my-12" />

        {loading ? (
          <p className="text-[var(--gai-grey-600)]">{t("common.loading")}</p>
        ) : !hero ? (
          <p className="text-[var(--gai-grey-500)]">{lang === "fr" ? "Aucune édition disponible pour l'instant." : "No edition available yet."}</p>
        ) : (
          <>
            {/* HERO — Édition principale */}
            <section
              className="grid lg:grid-cols-12 gap-10 items-start border border-[var(--gai-grey-200)] bg-[var(--gai-white)] p-6 md:p-10 relative"
              data-testid="magazine-hero"
            >
              <div className="absolute top-0 left-0 h-1 w-32 bg-[var(--gai-red)]" aria-hidden />

              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -inset-4 border border-[var(--gai-grey-300)]" aria-hidden />
                  <div className="relative aspect-[3/4] overflow-hidden bg-[var(--gai-grey-100)]">
                    <SafeImage
                      src={hero.cover_url}
                      alt={tr(hero.title)}
                      className="w-full h-full object-cover"
                      style={{ width: "100%", height: "100%" }}
                      loading="eager"
                      testid={`magazine-cover-${hero.id}`}
                    />
                  </div>
                  {hero.is_premium && (
                    <span
                      className="absolute -top-2 -right-2 px-4 py-2 bg-[var(--gai-red)] text-[var(--gai-white)] text-[0.7rem] tracking-[0.2em] uppercase font-semibold z-10"
                      data-testid={`magazine-price-${hero.id}`}
                    >
                      {fmt(hero.price, hero.currency)}
                    </span>
                  )}
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="overline text-[var(--gai-red)]" data-testid="magazine-hero-edition">
                  {hero.edition}
                </div>
                <h2 className="serif text-4xl md:text-5xl leading-[1.05] mt-3 text-[var(--gai-black)]" translate="no">
                  {tr(hero.title)}
                </h2>
                <p className="mt-2 text-sm md:text-base text-[var(--gai-grey-600)] italic">
                  {lang === "fr"
                    ? "Les origines du Magazine d'Affaires Panafricain"
                    : "The origins of the Pan-African Business Magazine"}
                </p>

                <div className="mt-6 w-16 h-[2px] bg-[var(--gai-red)]" aria-hidden />

                <p className="mt-6 text-[1rem] leading-[1.85] text-[var(--gai-grey-800)]">
                  {tr(hero.description)}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  {hero.pdf_url ? (
                    <Link
                      to={`/magazine/${hero.id}`}
                      className="gai-btn-gold text-sm"
                      data-testid={`magazine-open-${hero.id}`}
                    >
                      <BookOpen size={14} />
                      {lang === "fr" ? `Feuilleter ${hero.edition.split(" — ")[0] || "l'Édition N°0"}` : `Read ${hero.edition.split(" — ")[0] || "Edition N°0"}`}
                    </Link>
                  ) : (
                    <span className="text-[0.72rem] tracking-[0.2em] uppercase text-[var(--gai-red)]">
                      {t("magazine.noPdf")}
                    </span>
                  )}
                  {hero.is_premium && (
                    <button
                      onClick={() => buyMagazine(hero)}
                      disabled={busyId === hero.id}
                      className="gai-btn-outline text-sm"
                      data-testid={`magazine-buy-${hero.id}`}
                    >
                      <ShoppingBag size={14} /> {lang === "fr" ? "Acheter le PDF" : "Buy PDF"}
                    </button>
                  )}
                </div>

                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-[0.72rem] tracking-[0.18em] uppercase text-[var(--gai-grey-600)]">
                  {[
                    lang === "fr" ? "Le Magazine" : "The Magazine",
                    lang === "fr" ? "Lecteurs" : "Readers",
                    lang === "fr" ? "Marché" : "Market",
                    lang === "fr" ? "Communication & Régie" : "Communication",
                  ].map((label) => (
                    <div key={label} className="p-3 border border-[var(--gai-grey-200)] text-center bg-[var(--gai-white)]">
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Grille autres éditions (si présentes) */}
            {others.length > 0 && (
              <>
                <div className="mt-16 flex items-center gap-4">
                  <Archive size={16} className="text-[var(--gai-grey-500)]" />
                  <div className="overline">
                    {lang === "fr" ? "Autres éditions" : "Other editions"}
                  </div>
                  <div className="flex-1 h-[1px] bg-[var(--gai-grey-200)]" />
                </div>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="magazine-grid">
                  {others.map((m, idx) => (
                    <article
                      key={m.id}
                      className="gai-card gai-sharp overflow-hidden gai-fade-up bg-[var(--gai-white)]"
                      style={{ animationDelay: `${idx * 80}ms` }}
                      data-testid={`magazine-item-${m.id}`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden bg-[var(--gai-grey-100)]">
                        <SafeImage
                          src={m.cover_url}
                          alt=""
                          className="w-full h-full object-cover"
                          style={{ width: "100%", height: "100%" }}
                        />
                        {m.is_premium && (
                          <span
                            className="absolute top-4 right-4 px-3 py-1 bg-[var(--gai-red)] text-[var(--gai-white)] text-[0.65rem] tracking-[0.18em] uppercase font-semibold"
                            data-testid={`magazine-price-${m.id}`}
                          >
                            {fmt(m.price, m.currency)}
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="overline text-[var(--gai-red)] mb-2">{m.edition}</div>
                        <h3 className="serif text-2xl leading-tight text-[var(--gai-black)]" translate="no">
                          {tr(m.title)}
                        </h3>
                        {m.description && (
                          <p className="mt-3 text-sm text-[var(--gai-grey-600)] line-clamp-2">{tr(m.description)}</p>
                        )}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                          {m.pdf_url ? (
                            <Link to={`/magazine/${m.id}`} className="gai-btn-gold text-xs" data-testid={`magazine-open-${m.id}`}>
                              <BookOpen size={13} /> {t("magazine.read")}
                            </Link>
                          ) : (
                            <span className="text-[0.72rem] tracking-[0.2em] uppercase text-[var(--gai-red)]">
                              {t("magazine.noPdf")}
                            </span>
                          )}
                          {m.is_premium && (
                            <button
                              onClick={() => buyMagazine(m)}
                              disabled={busyId === m.id}
                              className="gai-btn-outline text-xs"
                              data-testid={`magazine-buy-${m.id}`}
                            >
                              <ShoppingBag size={13} /> {lang === "fr" ? "Acheter" : "Buy"}
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
